/**
 * QuizFocus — question-by-question quiz runner.
 *
 * Design source: /tmp/arianna-design/ios-reader/project/quiz.jsx QuizFocus
 *
 * Data model: index-based options (BundleQuizQuestion), numeric answers.
 * State machine (TASK-015 Phase 2 — free navigation): the caller owns ALL
 * session state (live answers, current viewed position, save/complete
 * status). This component is purely presentational + navigational — it
 * holds no answer state of its own (no more `pendingState`): the selection
 * shown for the current question is always `liveAnswers[q.id]`, exactly the
 * value the parent gave it. Every pill is clickable (forward AND backward —
 * navigation is free, there is no more "not yet reached" concept) and the
 * primary action button is never disabled except while `isCompleting`
 * (anti-double-submit on the final "Completa quiz").
 */

import * as React from 'react'
import { useMemo } from 'react'
import { clsx } from 'clsx'
import type { BundleQuizQuestion } from '@arianna/shared'
import { QuestionPill, Choice } from './primitives.js'

// ---------------------------------------------------------------------------
// Session shape consumed by QuizFocus
// ---------------------------------------------------------------------------

export interface QuizFocusSession {
  /** Currently VIEWED position (free navigation — not a forward-only cursor). */
  current_idx: number
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface QuizFocusProps {
  /** Ordered list of questions for this session, in position order. */
  questions: BundleQuizQuestion[]
  session: QuizFocusSession
  /** Live answers map keyed by question ID → option index (null = skipped) */
  liveAnswers: Record<string, number | null>
  /** True when launched from review mode (changes exit button label) */
  isReviewMode?: boolean
  onExit: () => void
  /** Selects an option for the CURRENT question. No guard — already-answered questions are freely revisable. */
  onSelectOption: (questionId: string, optionIdx: number) => void
  /** Jumps to any position, forward or backward. No guard. */
  onNavigate: (idx: number) => void
  /** Moves to the next question (clamped to the last position). Never blocked on missing selection — skipping is legitimate. */
  onAdvance: () => void
  /** Opens the "N domande saltate" confirmation (if needed) or finalizes the quiz directly. */
  onComplete: () => void
  /** True while a `complete` request is in flight — the only legitimate `disabled` state, shown with a spinner. */
  isCompleting?: boolean
  /**
   * Fires on a double-click on an option: the caller is expected to both
   * select it AND advance (or, on the last question, enter the complete
   * flow) — see QuizSession.tsx's handleDoubleClickOption. Optional: when
   * omitted, a double click behaves like two ordinary single clicks.
   */
  onDoubleClickOption?: (questionId: string, optionIdx: number) => void
}

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F']

// ---------------------------------------------------------------------------
// QuizFocus component
// ---------------------------------------------------------------------------

export function QuizFocus({
  questions,
  session,
  liveAnswers,
  isReviewMode = false,
  onExit,
  onSelectOption,
  onNavigate,
  onAdvance,
  onComplete,
  isCompleting = false,
  onDoubleClickOption,
}: QuizFocusProps) {
  const currentIdx = session.current_idx
  const total = questions.length
  const q = questions[currentIdx]

  const selectedOptionIdx: number | null = q != null ? (liveAnswers[q.id] ?? null) : null

  // Answered/skipped counts across the WHOLE quiz (every position is always
  // "in view range" now — free navigation removed the old forward-only
  // notion of "not yet reached"). answeredCount + skippedCount === total.
  const answeredCount = useMemo(
    () => Object.values(liveAnswers).filter((v) => v !== null && v !== undefined).length,
    [liveAnswers],
  )
  const skippedCount = useMemo(
    () => Object.values(liveAnswers).filter((v) => v === null).length,
    [liveAnswers],
  )

  if (q == null) return null

  const isLast = currentIdx === total - 1
  const isFirst = currentIdx === 0
  // Progress reflects overall completion (answeredCount/total), not linear
  // position — with free navigation, "how far forward you are" is no longer
  // a meaningful signal; "how much of the quiz is answered" is.
  const progressPct = total > 0 ? (answeredCount / total) * 100 : 0

  function handleSelectOption(optIdx: number) {
    onSelectOption(q!.id, optIdx)
  }

  function handlePrimaryAction() {
    if (isLast) {
      if (!isCompleting) onComplete()
    } else {
      onAdvance()
    }
  }

  return (
    <div className="flex flex-col h-full bg-bg font-sans overflow-hidden">
      {/* ── Top bar ───────────────────────────────────────────────── */}
      <div className="px-8 pt-[14px] pb-0 border-b border-line shrink-0">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center pb-[10px]">
          {/* Exit */}
          <button
            onClick={onExit}
            className="bg-transparent border-none text-ink-3 text-sm cursor-pointer font-sans flex items-center gap-[6px] p-0"
          >
            <span>←</span>
            {isReviewMode ? 'Esci' : 'Verifiche'}
          </button>

          {/* Centre: progress. TASK-014 dedup: the "Verifica · {bundleTitle}"
              breadcrumb duplicated the course shell's own header/tabs —
              removed; the back button (functionally useful — exits the
              session) stays. */}
          <div className="flex items-center gap-[10px] text-ink-3 whitespace-nowrap">
            <span className="font-mono text-sm">
              {currentIdx + 1}/{total}
            </span>
          </div>

          {/* Right: answered/skipped counters */}
          {/* design handoff: gap 18px */}
          <div className="flex items-center justify-end gap-[18px] whitespace-nowrap">
            <div className="flex items-center gap-[6px] text-ink-3">
              {/* design handoff: fontSize 11.5px */}
              <span className="text-[11.5px]">Risposte</span>
              <span className="font-mono text-sm text-ink-2 font-semibold">{answeredCount}</span>
            </div>
            <div className="flex items-center gap-[6px] text-ink-3">
              <span className="text-[11.5px]">Saltate</span>
              <span className="font-mono text-sm">{skippedCount}</span>
            </div>
          </div>
        </div>

        {/* Progress bar — sits flush at the bottom of the top bar */}
        <div className="h-[2px] bg-surface-3 overflow-hidden">
          <div
            className="h-full bg-ar-accent transition-[width] duration-[250ms] ease-[ease]"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* ── Question navigator pills — ALL clickable, any direction ──── */}
      <div className="flex gap-[6px] px-8 pt-3 pb-[14px] border-b border-line overflow-x-auto shrink-0">
        {questions.map((qq, i) => {
          const ans = liveAnswers[qq.id]
          return (
            <QuestionPill
              key={qq.id}
              n={i + 1}
              isCurrent={i === currentIdx}
              isAnswered={ans !== undefined && ans !== null}
              isSkipped={ans === null}
              onClick={() => onNavigate(i)}
            />
          )
        })}
      </div>

      {/* ── Question area ──────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto flex items-start justify-center px-8 pt-11 pb-8">
        {/* design handoff: maxWidth 820px */}
        <div className="max-w-[820px] w-full">
          {/* Eyebrow */}
          {/* design handoff: fontSize 10px (below text-xs=11px), use arbitrary */}
          <div className="flex items-center justify-center gap-2 mb-[22px] whitespace-nowrap">
            <span className="text-[10px] text-ink-3 tracking-[0.14em] uppercase font-semibold">
              Scelta multipla
            </span>
          </div>

          {/* Question stem */}
          {/* design handoff: fontSize 28px */}
          <h2 className="font-serif text-[28px] font-medium m-0 mb-9 tracking-[-0.02em] leading-[1.3] text-ink text-center">
            {q.stem}
          </h2>

          {/* Options */}
          <div className="flex flex-col gap-[10px] mb-8">
            {q.options.map((optText, i) => (
              <Choice
                key={i}
                letter={LETTERS[i] ?? String(i)}
                text={optText}
                selected={selectedOptionIdx === i}
                onClick={() => handleSelectOption(i)}
                onDoubleClick={
                  onDoubleClickOption != null ? () => onDoubleClickOption(q.id, i) : undefined
                }
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center px-8 py-4 border-t border-line shrink-0 bg-bg">
        {/* Left: Indietro — disabled only at position 0 */}
        <div>
          <button
            onClick={() => onNavigate(currentIdx - 1)}
            disabled={isFirst}
            className={clsx(
              'px-[18px] py-[10px] text-sm font-medium rounded-[9px] cursor-pointer font-sans',
              isFirst
                ? 'bg-surface text-ink-4 border border-line opacity-55 cursor-not-allowed'
                : 'bg-surface text-ink-2 border border-line',
            )}
          >
            ← Indietro
          </button>
        </div>

        {/* Centre: intentionally empty — the old "A · B · C · D per
            scegliere" keyboard hint promised shortcuts that don't exist and
            was removed (task brief). */}
        <div />

        {/* Right: primary action — never disabled except mid-submit on the
            final "Completa quiz" (anti-double-submit). */}
        <div className="justify-self-end">
          <button
            onClick={handlePrimaryAction}
            disabled={isLast && isCompleting}
            className={clsx(
              /* design handoff: fontSize 13.5px */
              'px-[22px] py-[10px] text-[13.5px] font-semibold rounded-[9px] cursor-pointer font-sans whitespace-nowrap inline-flex items-center gap-2',
              isLast && isCompleting
                ? 'bg-ink-4 text-bg border border-ink-4 opacity-55 cursor-not-allowed'
                : 'bg-ink text-bg border border-ink opacity-100',
            )}
          >
            {isLast && isCompleting && (
              <span
                aria-hidden="true"
                className="inline-block w-[13px] h-[13px] rounded-full border-2 border-current border-t-transparent animate-spin"
              />
            )}
            {isLast ? (isCompleting ? 'Concludendo…' : 'Completa quiz') : 'Avanti →'}
          </button>
        </div>
      </div>
    </div>
  )
}
